import dayjs from 'dayjs';

import type { EmergencyFusePayload, EmergencyFuseResponse, EmergencyFuseState } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function getDefaultFuseState(): EmergencyFuseState {
  return {
    pauseIssuance: false,
    pauseOrders: false,
    reason: null,
  };
}

export function normalizeFuseState(raw: EmergencyFuseResponse | unknown): EmergencyFuseState {
  const source =
    isRecord(raw) && isRecord(raw.data)
      ? raw.data
      : isRecord(raw) && isRecord(raw.state)
        ? raw.state
        : raw;

  const fallback = getDefaultFuseState();

  if (!isRecord(source)) {
    return fallback;
  }

  return {
    pauseIssuance:
      typeof source.pauseIssuance === 'boolean'
        ? source.pauseIssuance
        : typeof source.pause_issuance === 'boolean'
          ? source.pause_issuance
          : fallback.pauseIssuance,
    pauseOrders:
      typeof source.pauseOrders === 'boolean'
        ? source.pauseOrders
        : typeof source.pause_orders === 'boolean'
          ? source.pause_orders
          : fallback.pauseOrders,
    reason: source.reason === null || source.reason === undefined ? null : String(source.reason),
    configVersion:
      source.configVersion === undefined && source.config_version === undefined
        ? undefined
        : Number(source.configVersion ?? source.config_version),
    updatedBy:
      source.updatedBy !== undefined
        ? String(source.updatedBy)
        : source.updated_by !== undefined
          ? String(source.updated_by)
          : undefined,
    updatedAt:
      source.updatedAt !== undefined
        ? String(source.updatedAt)
        : source.updated_at !== undefined
          ? String(source.updated_at)
          : undefined,
  };
}

export function buildFusePayload(
  draft: EmergencyFuseState,
  original: EmergencyFuseState,
  reason: string
): EmergencyFusePayload {
  const payload: EmergencyFusePayload = {
    reason: reason.trim(),
  };

  if (draft.pauseIssuance !== original.pauseIssuance) {
    payload.pauseIssuance = draft.pauseIssuance;
  }

  if (draft.pauseOrders !== original.pauseOrders) {
    payload.pauseOrders = draft.pauseOrders;
  }

  return payload;
}

export function isFuseStateEqual(left: EmergencyFuseState, right: EmergencyFuseState) {
  return left.pauseIssuance === right.pauseIssuance && left.pauseOrders === right.pauseOrders;
}

export function formatUpdatedAt(value?: string | null) {
  if (!value) return '--';

  const date = dayjs(value);

  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';

  if (typeof error === 'string') return error;

  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;
    if (typeof error.error === 'string') return error.error;

    if (isRecord(error.response) && isRecord(error.response.data)) {
      const data = error.response.data;

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  return String(error);
}
